/**
 * Choosing how many installments to settle in one payment.
 *
 * The loan page used to expose exactly one «پرداخت» button — on the next
 * unpaid installment — so a member catching up on five overdue months had to
 * walk the whole pay flow five times. Everything here exists to let them pick
 * «۵ تای اول» or «کلش» and pay once.
 *
 * Selection is a PREFIX, held as a count of leading unpaid installments rather
 * than a set of ids. A قرض‌الحسنه schedule is settled front-to-back: paying
 * قسط ۷ while ۳ is still overdue leaves the fund's arrears reporting saying
 * one thing and the member's ledger another. Modelling it as a count makes
 * that impossible to express instead of merely discouraged, and it is exactly
 * the three cases the request asked for — one, the overdue run, or all of it.
 */

export interface InstallmentRow {
    id: number
    amount: number
    index: number
    is_paid: boolean
    /**
     * Absent — not false — on another member's installment when the fund
     * hides those (`LimitedInstallmentSerializer` strips the key). Optional
     * here so the difference is expressible.
     */
    is_delayed?: boolean
    due_date?: string
    jalali_due_date?: string
    /** ISO datetime of the repayment cash-in; null while unpaid */
    pay_time?: string | null
    /**
     * The manager's «توضیحات» on the repayment, off the cash-in — the same
     * column and the same text the transactions tab shows. Empty on a
     * gateway-paid installment and stripped, like `is_delayed`, on another
     * member's row in a fund that hides those.
     */
    tag?: string
}

/** How a settled installment landed against its due date. */
export interface InstallmentTiming {
    kind: 'early' | 'late'
    days: number
}

/**
 * Was this installment settled before its due date, or after it — and by how
 * much?
 *
 * The schedule used to say only «پرداخت شده», which is the same badge for a
 * member who paid a month ahead and one who paid three months late. Both
 * facts are already in the payload (`pay_time` against `due_date`), and both
 * now feed امتیاز عضو, so the screen that shows the repayment should show
 * what the score is reading.
 *
 * Whole local days on both sides, the same convention the backend scores by:
 * paying at 23:00 on the due date is on time, not «۱ روز تأخیر».
 *
 * Returns null when the timing is not ours to state — see `is_delayed`.
 */
export const paymentTiming = (row: InstallmentRow): InstallmentTiming | null => {
    if (!row.is_paid || !row.due_date || !row.pay_time) return null
    // The backend deliberately withholds `is_delayed` for members who may not
    // see how another member is doing. Deriving lateness from the dates would
    // walk straight around that, so no flag means no timing either.
    if (row.is_delayed === undefined) return null
    const due = new Date(`${row.due_date}T00:00:00`)
    const paid = new Date(row.pay_time)
    if (Number.isNaN(due.getTime()) || Number.isNaN(paid.getTime())) return null
    paid.setHours(0, 0, 0, 0)
    const days = Math.round((paid.getTime() - due.getTime()) / 86_400_000)
    if (days === 0) return null
    return days > 0 ? { kind: 'late', days } : { kind: 'early', days: -days }
}

/**
 * Has this installment come due?
 *
 * `is_delayed` is derived from `Installment.status`, which a nightly cron
 * maintains — it is stale between midnight and the ~02:00 run, and after any
 * missed beat. `due_date <= today` is the same rule the backend applies when
 * it builds the پرداختنی‌ها list (`GroupAllPayment.init`, "OQ-1"), so prefer
 * it and keep the flag as the fallback for a payload without a due date.
 */
export const isOverdue = (row: InstallmentRow, now: Date = new Date()) => {
    // `!!` because the flag is now optional: a payload that withheld it tells
    // us nothing, which is the same answer the old `boolean` type produced at
    // runtime when the key was missing.
    if (!row.due_date) return !!row.is_delayed
    const due = new Date(`${row.due_date}T00:00:00`)
    if (Number.isNaN(due.getTime())) return !!row.is_delayed
    const today = new Date(now)
    today.setHours(0, 0, 0, 0)
    return due.getTime() <= today.getTime()
}

/** The unpaid installments, in schedule order — the only selectable ones. */
export const unpaidOf = (rows: InstallmentRow[]): InstallmentRow[] =>
    rows.filter((row) => !row.is_paid).sort((a, b) => a.index - b.index)

/**
 * How many leading unpaid installments are already due.
 *
 * Counted as a run from the front and stopped at the first one that is not
 * due: that keeps the answer a prefix, so «اقساط عقب‌افتاده» stays a legal
 * selection. A schedule cannot have a due installment behind an undue one
 * anyway — if it somehow does, the later one is simply not offered.
 */
export const overdueRun = (unpaid: InstallmentRow[], now?: Date): number => {
    let count = 0
    for (const row of unpaid) {
        if (!isOverdue(row, now)) break
        count += 1
    }
    return count
}

/**
 * The new selection size after tapping the installment at `position`.
 *
 * Tapping the last selected row gives it back (so the row you just added is
 * also the one you can remove); tapping anything else extends the selection
 * through it.
 */
export const countAfterTap = (selectedCount: number, position: number) =>
    selectedCount === position + 1 ? position : position + 1

/** Sum owed for the first `count` unpaid installments. */
export const selectionTotal = (unpaid: InstallmentRow[], count: number) =>
    unpaid.slice(0, count).reduce((sum, row) => sum + (row.amount || 0), 0)

/** The rows a selection of `count` actually covers. */
export const selectedRows = (unpaid: InstallmentRow[], count: number) =>
    unpaid.slice(0, Math.max(0, Math.min(count, unpaid.length)))

/**
 * «قسط ۳» / «قسط ۳ تا ۷» — what the member is about to pay.
 *
 * Uses `index`, the installment's own 1-based number in the schedule, not its
 * position in the unpaid list: on a loan with the first two already settled,
 * the first selectable row is قسط ۳ and calling it «قسط ۱» would be a lie.
 */
export const selectionLabel = (
    unpaid: InstallmentRow[],
    count: number,
    format: (value: number) => string
) => {
    const rows = selectedRows(unpaid, count)
    if (rows.length === 0) return ''
    const first = rows[0].index
    const last = rows[rows.length - 1].index
    return first === last
        ? `قسط ${format(first)}`
        : `قسط ${format(first)} تا ${format(last)}`
}
